// Utility functions for dog-related calculations

/**
 * Calculate age from date of birth
 * @param {Date|string} dateOfBirth - Date of birth
 * @returns {Object} { years, months, totalMonths, displayText }
 */
export const calculateAgeFromDOB = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    
    const dob = new Date(dateOfBirth);
    const now = new Date();
    
    if (isNaN(dob.getTime()) || dob > now) {
        return null;
    }
    
    let years = now.getFullYear() - dob.getFullYear();
    let months = now.getMonth() - dob.getMonth();
    let days = now.getDate() - dob.getDate();
    
    // Adjust for negative days
    if (days < 0) {
        months--;
        // Get days in previous month
        const prevMonth = new Date(now.getFullYear(), now.getMonth(), 0);
        days += prevMonth.getDate();
    }
    
    // Adjust for negative months
    if (months < 0) {
        years--;
        months += 12;
    }
    
    const totalMonths = years * 12 + months;
    
    let displayText;
    if (years === 0) {
        // Puppy - show months
        if (months === 0) {
            displayText = days === 1 ? "1 day old" : `${days} days old`;
        } else if (months === 1) {
            displayText = "1 month old";
        } else {
            displayText = `${months} months old`;
        }
    } else if (years === 1 && months === 0) {
        displayText = "1 year old";
    } else if (months === 0) {
        displayText = `${years} years old`;
    } else {
        displayText = `${years} years, ${months} months old`;
    }
    
    return {
        years,
        months,
        days,
        totalMonths,
        displayText,
        isPuppy: years < 1
    };
};

/**
 * Format a dog's age as a Spanish string, using calculatedAge virtual,
 * dateOfBirth, or the legacy numeric age field as fallbacks.
 */
export const formatDogAge = (dog) => {
    if (!dog) return 'Edad desconocida';
    if (dog.calculatedAge?.displayText) return dog.calculatedAge.displayText;
    if (dog.dateOfBirth) {
        const data = calculateAgeFromDOB(dog.dateOfBirth);
        if (data) {
            const { years, months } = data;
            if (years === 0) {
                return months === 0 ? 'Menor de 1 mes' :
                       months === 1 ? '1 mes' : `${months} meses`;
            }
            return years === 1 && months === 0 ? '1 año' :
                   months === 0 ? `${years} años` : `${years} años, ${months} meses`;
        }
    }
    const age = dog.age;
    if (!age && age !== 0) return 'Edad desconocida';
    return `${age} año${age !== 1 ? 's' : ''}`;
};

/**
 * Calculate size tag based on weight
 * @param {number} weight - Weight in kg
 * @returns {Object} { tag, label, description }
 */
export const calculateSizeTag = (weight) => {
    if (!weight || weight <= 0) {
        return {
            tag: '?',
            label: 'Unknown',
            description: 'No weight available'
        };
    }
    
    if (weight <= 10.9) {
        return {
            tag: 'S',
            label: 'Small',
            description: '0kg - 10.9kg'
        };
    } else if (weight <= 20) {
        return {
            tag: 'M',
            label: 'Medium',
            description: '11kg - 20kg'
        };
    } else if (weight <= 35) {
        return {
            tag: 'L',
            label: 'Large',
            description: '21kg - 35kg'
        };
    } else {
        return {
            tag: 'XL',
            label: 'Extra Large',
            description: 'More than 35kg'
        };
    }
};

/**
 * Format date for input field (YYYY-MM-DD)
 * @param {Date|string} date 
 * @returns {string}
 */
export const formatDateForInput = (date) => {
    if (!date) return '';
    
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
};

/**
 * Get maximum allowed date of birth (today)
 * @returns {string} YYYY-MM-DD format
 */
export const getMaxDateOfBirth = () => {
    return formatDateForInput(new Date());
};

/**
 * Get minimum allowed date of birth (30 years ago)
 * @returns {string} YYYY-MM-DD format
 */
export const getMinDateOfBirth = () => {
    const minDate = new Date();
    minDate.setFullYear(minDate.getFullYear() - 30);
    return formatDateForInput(minDate);
};